import { DefaultTheme } from "styled-components";
import { ITheme } from "./ITheme";

const spacingScale = {
    sp01: 4,
    sp02: 8,
    sp03: 12,
    sp04: 16,
    sp05: 24,
    sp06: 32,
    sp07: 48,
    sp08: 64,
    sp09: 96,
} as const;

const toPx = (value: number): string => `${value}px`;

export const theme: ITheme = {
    spacing: spacingScale.sp01,
    spaceUnits: {
        ...spacingScale,
    },
    spacePx: {
        sp01: toPx(spacingScale.sp01),
        sp02: toPx(spacingScale.sp02),
        sp03: toPx(spacingScale.sp03),
        sp04: toPx(spacingScale.sp04),
        sp05: toPx(spacingScale.sp05),
        sp06: toPx(spacingScale.sp06),
        sp07: toPx(spacingScale.sp07),
        sp08: toPx(spacingScale.sp08),
        sp09: toPx(spacingScale.sp09),
    },
    spacingScale: {
        iconGap: toPx(spacingScale.sp01),
        tightPadding: toPx(spacingScale.sp02),
        formFieldGap: toPx(spacingScale.sp03),
        cardPadding: toPx(spacingScale.sp04),
        sectionGap: toPx(spacingScale.sp05),
        betweenCards: toPx(spacingScale.sp06),
        majorSectionBreak: toPx(spacingScale.sp07),
        pageMargin: toPx(spacingScale.sp08),
        heroSpacing: toPx(spacingScale.sp09),
    },
    palette: {
        white: "#ffffff",
        black: "#000000",
        grey: "#6c757d",

        accent: {
            light: "#ea8522",
            dark: "#162242"
        },
        background: {
            transparent: "transparent",
            main: "#ffffff",
            light: "#f8f9fa",
            dark: "#555A63",
        },
        text: {
            main: "#727171",
            light: "#f5f5f5",
            dark: "#3b3b3b",
            disabled: "#898E96",
            hint: "#8A8D98",
            secondary: "#687087",
            strong: "#111827",
        },
        disabled: {
            light: "#E6E7E9",
            dark: "#898E96",
        },
        link: {
            main: "#1D73B7",
            light: "#93BAD9",
            dark: "#1B5C99",
            text: "#ffffff",
        },
        information: {
            main: "#1D73B7",
            light: "#93BAD9",
            dark: "#1B5C99",
            text: "#ffffff",
        },
        success: {
            main: "#28a745",
            light: "#A8E6BC",
            dark: "#1E7E37",
            text: "#ffffff",
        },
        danger: {
            main: "#F44336",
            light: "#F9A8A3",
            dark: "#B23328",
            text: "#ffffff",
        },
        warning: {
            main: "#ffc107",
            light: "#ffda07",
            dark: "#EAB308",
            text: "#ffffff",
        },

        divider: {
            main: "#727171",
            light: "#f5f5f5",
            dark: "#333333",
            subtle: "#e5e7eb",
        },
        tab: {
            main: "#225683",
            light: "#f2f6ff",
            dark: "#3b3b3b",
        },
        icon: {
            muted: "#A9A9A9",
            parentAccent: "#dc4629",
        },
    },

    shape: {
        borderRadiusSm: spacingScale.sp02,
        borderRadiusMd: spacingScale.sp03,
        borderRadiusLg: spacingScale.sp04,
    },

    font: {
        family: "Poppins",

        sizes: {
            xs: ".5rem",
            sm: ".75rem",
            md: "1rem",
            lg: "1.25rem",
            xl: "1.75rem",
            xxl: "2rem",
        },

        weights: {
            light: 200,
            regular: 400,
            semiBold: 600,
            bold: 700,
        },
    },

    fontFamily: "Poppins",

    shadowTypes: {
        buttonHover: "0 2px 4px 0 rgba(0, 0, 0, 0.5)",
        buttonFocus: "0 0 0px 2px #fff, 0 0 3px 4px rgba(234, 133, 34, 0.5)",
        popover: "0 0 15px 5px rgba(0, 0, 0, 0.25)",
        imageHover: "0 2px 6px 0 rgba(0, 0, 0, 0.5)",
    },

    typography: {
        fontFamily: `Poppins`,
    },
};

/**
 * Dark theme. Spacing, shape, font, and shadows are shared with the light
 * theme (only color changes with theme) -- only `palette` is overridden
 * below.
 *
 * Confidence levels, so a designer can tell what needs review:
 *
 * TIER 1 -- reused as-is from the ASP.NET Auth side's already-designed
 * :root.dark palette (Auth/wwwroot/css/constant.css). These are
 * brand-approved values, not guesses: grey, accent.light/dark,
 * background.main, text.main, disabled.dark, link.main, information.main,
 * success.main, danger.main, warning.main.
 *
 * TIER 2 -- derived with an explicit, mechanical rule because leaving the
 * light-theme value unchanged would be a real legibility bug (e.g. a
 * near-black "dark text" color left as-is would be nearly invisible on a
 * dark background). Each has an inline comment explaining the rule used.
 * These should be reviewed by a designer, but were not left broken.
 *
 * TIER 3 -- intentionally left identical to the light theme. These are
 * lower-traffic decorative variants (the light/dark/text sub-shades of
 * link/information/success/danger/warning, plus tab and divider fine
 * shades) where an unreviewed guess seemed riskier than "unchanged." None
 * of these are large text blocks, so the risk is "looks slightly off,"
 * not "illegible."
 */
export const darkTheme: ITheme = {
    ...theme,
    palette: {
        // TIER 1: reused from ASP.NET's designed dark palette
        white: "#ffffff", // unchanged -- literal white, used for text/icons on colored surfaces, not a background
        black: "#000000", // unchanged -- literal black, same reasoning
        grey: "#A9ACB0",

        accent: {
            light: "#F0954A",
            dark: "#5A70A8",
        },
        background: {
            transparent: "transparent",
            main: "#14161C",
            // TIER 1: ASP.NET's --color-surface, used there as the elevated/card
            // surface tone one step up from the page background
            light: "#1E222B",
            // TIER 2 (derived): slightly darker than `main`, for the rare need
            // of an even-deeper recessed surface. No ASP.NET equivalent exists;
            // this mirrors the light theme's background.dark being a distinct
            // third shade rather than a copy of `main`.
            dark: "#0B0C10",
        },
        text: {
            main: "#C7C9CE",
            // TIER 3: unchanged. Used as light-colored text on colored surfaces
            // (e.g. the navy header), which stays legible in both themes since
            // that surface stays dark-ish either way.
            light: "#f5f5f5",
            // TIER 2 (derived): text.dark is meant to be the *most prominent*
            // text color. In light mode that's near-black; the dark-mode
            // equivalent for "most prominent" is near-white, so this inverts
            // rather than copies the light-mode value.
            dark: "#F5F6F7",
            // TIER 2 (derived): dimmer than text.main but still visible against
            // a dark background -- a lightened version of ASP.NET's own
            // disabled-dark tone rather than an invented color.
            disabled: "#6B7280",
            // TIER 3: unchanged. Already a mid-grey with some legibility on
            // both light and dark backgrounds.
            hint: "#8A8D98",
            // TIER 1: reused from ASP.NET's --color-muted dark value
            secondary: "#9A9DA8",
            // TIER 2 (derived): same inversion rule as text.dark -- "strongest"
            // emphasis text goes near-black to near-white.
            strong: "#FFFFFF",
        },
        disabled: {
            // TIER 1: reused from ASP.NET's --color-grey-light dark value,
            // which plays the same "muted structural" role there
            light: "#2A2E38",
            // TIER 1: reused from ASP.NET's --color-muted dark value
            dark: "#9A9DA8",
        },
        // TIER 3: link/information/success/danger/warning main colors are
        // TIER 1 (ASP.NET-sourced); their light/dark/text sub-shades are
        // left unchanged from the light theme (decorative accents/chips,
        // not large text blocks -- lower risk if unreviewed).
        link: {
            main: "#6EA8FE",
            light: "#93BAD9",
            dark: "#1B5C99",
            text: "#ffffff",
        },
        information: {
            main: "#4FA3E0",
            light: "#93BAD9",
            dark: "#1B5C99",
            text: "#ffffff",
        },
        success: {
            main: "#4CD37B",
            light: "#A8E6BC",
            dark: "#1E7E37",
            text: "#ffffff",
        },
        danger: {
            main: "#FF6B5E",
            light: "#F9A8A3",
            dark: "#B23328",
            text: "#ffffff",
        },
        warning: {
            main: "#FFCF4D",
            light: "#ffda07",
            dark: "#EAB308",
            text: "#ffffff",
        },

        divider: {
            // TIER 1: reused from ASP.NET's --color-grey dark value
            main: "#A9ACB0",
            // TIER 2 (derived): divider.light is meant to be a *subtle* border
            // against the page background. Left unchanged (#f5f5f5) it would
            // become a harsh bright line against a dark page, so this uses
            // ASP.NET's --color-grey-light dark value instead, which plays the
            // same "subtle structural" role there.
            light: "#2A2E38",
            // TIER 2 (derived): same inversion rule as text.dark -- divider.dark
            // is meant to be a *higher-contrast* divider, so it inverts toward
            // light rather than staying near-black.
            dark: "#E5E7EB",
            // TIER 2 (derived): same "subtle structural" role as divider.light,
            // reusing the same ASP.NET-sourced tone for consistency.
            subtle: "#2A2E38",
        },
        // TIER 2 (derived): tab.main/light lighten and desaturate to stay
        // visible against a dark page; tab.dark follows the same
        // near-black-to-near-white inversion rule as text.dark, since it's
        // used as text color on the tab surface.
        tab: {
            main: "#3D7DB0",
            light: "#1A2B3D",
            dark: "#F5F6F7",
        },
        icon: {
            // TIER 1: reused from ASP.NET's --color-muted dark value
            muted: "#9A9DA8",
            // TIER 2 (derived, lower confidence): no ASP.NET field maps to this
            // one directly. Uses ASP.NET's --color-tertiary dark value, a
            // different accent-red family than the light-mode #dc4629 but the
            // closest "distinct alert-adjacent accent" already in the design
            // system -- worth a second look from a designer.
            parentAccent: "#FF5C7A",
        },
    },
};